/**
 * Default permissions for each role.
 * These are the baseline permissions that apply when no overrides are set.
 */

import {
  WorkflowPermissions,
  Role,
  getPermissionValue,
} from "../types/workflowPermissions";

/**
 * Default permissions by role
 */
export const DEFAULT_PERMISSIONS: Record<Role, WorkflowPermissions> = {
  Owner: {
    calendar: {
      scope: "all",
      canManageTimeBlocks: true,
      canDeleteTimeBlocks: true,
      canManageAppointmentTags: true,
    },
    products: {
      canView: true,
      canEdit: true,
    },
    clients: {
      canView: true,
      canDelete: true,
    },
    packages: {
      canManageClientPackages: true,
    },
    marketing: {
      canView: true,
      canCreate: true,
      canCancel: true,
    },
    reports: {
      scope: "all",
      canDownload: true,
      canViewSalesSummary: true,
      earningsSummaryScope: "all",
      canShareReports: true,
    },
    payroll: {
      clockScope: "all",
      canSyncToPayroll: true,
    },
    payments: {
      canView: true,
      canIssueRefunds: true,
      canManageTerminals: true,
    },
    cashRegister: {
      canView: true,
      canManage: true,
    },
    notifications: {
      scope: "all",
    },
    settingsNotifications: {
      scope: "all",
    },
    settingsStaff: {
      scope: "all",
      canEdit: true,
    },
    settingsStudio: {
      canView: true,
      canEdit: true,
      canSeeBilling: true,
    },
    settingsServices: {
      scope: "all",
      canManageServices: true,
      canViewAdjustmentForms: true,
      canManageAdjustmentForms: true,
    },
    settingsSSG: {
      canAccess: true,
    },
    settingsDeposits: {
      canAccess: true,
    },
    settingsDiscounts: {
      canManagePresets: true,
      canDeletePresets: true,
    },
    settingsCommissions: {
      scope: "all",
      canEdit: true,
      canAssignProductCommission: true,
    },
    settingsPayroll: {
      canAccess: true,
    },
    staffTiers: {
      canCreateTiers: true,
      canAssignStaffToTiers: true,
      canDeleteTiers: true,
    },
    formsAndWaivers: {
      canViewClientForms: true,
      canManageClientForms: true,
      canViewTemplates: true,
      canManageTemplates: true,
    },
    salonDocuments: {
      canViewDocuments: true,
      canManageDocuments: true,
    },
  },

  Manager: {
    calendar: {
      scope: "all",
      canManageTimeBlocks: true,
      canDeleteTimeBlocks: true,
      canManageAppointmentTags: true,
    },
    products: {
      canView: true,
      canEdit: true,
    },
    clients: {
      canView: true,
      canDelete: true,
    },
    packages: {
      canManageClientPackages: true,
    },
    marketing: {
      canView: true,
      canCreate: true,
      canCancel: true,
    },
    reports: {
      scope: "all",
      canDownload: true,
      canViewSalesSummary: true,
      earningsSummaryScope: "all",
      canShareReports: true,
    },
    payroll: {
      clockScope: "all",
      canSyncToPayroll: true,
    },
    payments: {
      canView: true,
      canIssueRefunds: true,
      canManageTerminals: true,
    },
    cashRegister: {
      canView: true,
      canManage: true,
    },
    notifications: {
      scope: "all",
    },
    settingsNotifications: {
      scope: "all",
    },
    settingsStaff: {
      scope: "all",
      canEdit: true,
    },
    settingsStudio: {
      canView: true,
      canEdit: true,
      canSeeBilling: false,
    },
    settingsServices: {
      scope: "all",
      canManageServices: true,
      canViewAdjustmentForms: true,
      canManageAdjustmentForms: true,
    },
    settingsSSG: {
      canAccess: true,
    },
    settingsDeposits: {
      canAccess: true,
    },
    settingsDiscounts: {
      canManagePresets: true,
      canDeletePresets: true,
    },
    settingsCommissions: {
      scope: "all",
      canEdit: true,
      canAssignProductCommission: true,
    },
    settingsPayroll: {
      canAccess: true,
    },
    staffTiers: {
      canCreateTiers: true,
      canAssignStaffToTiers: true,
      canDeleteTiers: true,
    },
    formsAndWaivers: {
      canViewClientForms: true,
      canManageClientForms: true,
      canViewTemplates: true,
      canManageTemplates: true,
    },
    salonDocuments: {
      canViewDocuments: true,
      canManageDocuments: true,
    },
  },

  Employee: {
    calendar: {
      scope: "own",
      canManageTimeBlocks: true,
      canDeleteTimeBlocks: true,
      canManageAppointmentTags: false,
    },
    products: {
      canView: true,
      canEdit: false,
    },
    clients: {
      canView: true,
      canDelete: false,
    },
    packages: {
      canManageClientPackages: false,
    },
    marketing: {
      canView: false,
      canCreate: false,
      canCancel: false,
    },
    reports: {
      scope: "own",
      canDownload: false,
      canViewSalesSummary: false,
      earningsSummaryScope: "own",
      canShareReports: false,
    },
    payroll: {
      clockScope: "own",
      canSyncToPayroll: false,
    },
    payments: {
      canView: false,
      canIssueRefunds: false,
      canManageTerminals: false,
    },
    cashRegister: {
      canView: false,
      canManage: false,
    },
    notifications: {
      scope: "own",
    },
    settingsNotifications: {
      scope: "own",
    },
    settingsStaff: {
      scope: "own",
      canEdit: false,
    },
    settingsStudio: {
      canView: false,
      canEdit: false,
      canSeeBilling: false,
    },
    settingsServices: {
      scope: "own",
      canManageServices: false,
      canViewAdjustmentForms: false,
      canManageAdjustmentForms: false,
    },
    settingsSSG: {
      canAccess: false,
    },
    settingsDeposits: {
      canAccess: false,
    },
    settingsDiscounts: {
      canManagePresets: true,
      canDeletePresets: false,
    },
    settingsCommissions: {
      scope: "own",
      canEdit: false,
      canAssignProductCommission: false,
    },
    settingsPayroll: {
      canAccess: true,
    },
    staffTiers: {
      canCreateTiers: false,
      canAssignStaffToTiers: false,
      canDeleteTiers: false,
    },
    formsAndWaivers: {
      canViewClientForms: true,
      canManageClientForms: true,
      canViewTemplates: true,
      canManageTemplates: false,
    },
    salonDocuments: {
      canViewDocuments: true,
      canManageDocuments: true,
    },
  },

  Assistant: {
    calendar: {
      scope: "all",
      canManageTimeBlocks: true,
      canDeleteTimeBlocks: true,
      canManageAppointmentTags: false,
    },
    products: {
      canView: true,
      canEdit: true,
    },
    clients: {
      canView: true,
      canDelete: true,
    },
    packages: {
      canManageClientPackages: false,
    },
    marketing: {
      canView: false,
      canCreate: false,
      canCancel: false,
    },
    reports: {
      scope: "none",
      canDownload: false,
      canViewSalesSummary: false,
      earningsSummaryScope: "own",
      canShareReports: false,
    },
    payroll: {
      clockScope: "own",
      canSyncToPayroll: false,
    },
    payments: {
      canView: true,
      canIssueRefunds: false,
      canManageTerminals: false,
    },
    cashRegister: {
      canView: false,
      canManage: false,
    },
    notifications: {
      scope: "all",
    },
    settingsNotifications: {
      scope: "own",
    },
    settingsStaff: {
      scope: "own",
      canEdit: false,
    },
    settingsStudio: {
      canView: false,
      canEdit: false,
      canSeeBilling: false,
    },
    settingsServices: {
      scope: "all",
      canManageServices: false,
      canViewAdjustmentForms: true,
      canManageAdjustmentForms: false,
    },
    settingsSSG: {
      canAccess: false,
    },
    settingsDeposits: {
      canAccess: false,
    },
    settingsDiscounts: {
      canManagePresets: true,
      canDeletePresets: false,
    },
    settingsCommissions: {
      scope: "none",
      canEdit: false,
      canAssignProductCommission: false,
    },
    settingsPayroll: {
      canAccess: true,
    },
    staffTiers: {
      canCreateTiers: false,
      canAssignStaffToTiers: false,
      canDeleteTiers: false,
    },
    formsAndWaivers: {
      canViewClientForms: true,
      canManageClientForms: false,
      canViewTemplates: true,
      canManageTemplates: false,
    },
    salonDocuments: {
      canViewDocuments: false,
      canManageDocuments: false,
    },
  },
};

/**
 * Role descriptions for display in the UI
 */
export const ROLE_DESCRIPTIONS: Record<
  Role,
  { name: string; description: string }
> = {
  Owner: {
    name: "Owner",
    description:
      "Full access to entire organization, including billing and settings",
  },
  Manager: {
    name: "Manager",
    description: "Can manage staff, clients, and most settings",
  },
  Employee: {
    name: "Employee",
    description: "Standard staff member with limited access",
  },
  Assistant: {
    name: "Assistant",
    description: "Front desk or support role with calendar and client access",
  },
};

/**
 * Get default permissions for a role
 */
export function getDefaultPermissions(role: Role): WorkflowPermissions {
  return DEFAULT_PERMISSIONS[role];
}

/**
 * Check if a permission value differs from the default
 */
export function isOverridden<T extends keyof WorkflowPermissions>(
  role: Role,
  workflow: T,
  key: keyof WorkflowPermissions[T],
  value: unknown,
): boolean {
  const defaultValue = DEFAULT_PERMISSIONS[role][workflow][key];
  return defaultValue !== value;
}

/**
 * Get all overridden permissions for a role.
 * Compares current permissions against defaults and returns only the differences.
 */
export function getOverrides(
  role: Role,
  permissions: WorkflowPermissions,
): Partial<WorkflowPermissions> {
  const overrides: Partial<WorkflowPermissions> = {};
  const defaults = DEFAULT_PERMISSIONS[role];

  for (const workflow of Object.keys(
    permissions,
  ) as (keyof WorkflowPermissions)[]) {
    const workflowPerms = permissions[workflow];
    const workflowOverrides: Record<string, boolean | string> = {};

    for (const key of Object.keys(workflowPerms)) {
      const currentValue = getPermissionValue(permissions, workflow, key);
      const defaultValue = getPermissionValue(defaults, workflow, key);

      if (currentValue !== defaultValue && currentValue !== undefined) {
        workflowOverrides[key] = currentValue;
      }
    }

    if (Object.keys(workflowOverrides).length > 0) {
      (overrides as Record<string, typeof workflowOverrides>)[workflow] =
        workflowOverrides;
    }
  }

  return overrides;
}

/**
 * Merge default permissions with overrides.
 * Returns a complete WorkflowPermissions object with defaults filled in.
 */
export function mergeWithDefaults(
  role: Role,
  overrides: Partial<WorkflowPermissions>,
): WorkflowPermissions {
  const defaults = DEFAULT_PERMISSIONS[role];
  const merged = JSON.parse(JSON.stringify(defaults)) as WorkflowPermissions;

  for (const workflow of Object.keys(
    overrides,
  ) as (keyof WorkflowPermissions)[]) {
    const workflowOverrides = overrides[workflow];
    if (workflowOverrides) {
      // Initialize {} when the workflow isn't in this build's DEFAULT_PERMISSIONS
      // (backend may declare workflows the FE catalog hasn't added yet, e.g.
      // loyalty / giftCards). Without this, Object.assign(undefined, ...) throws
      // "Cannot convert undefined or null to object" and the settings page
      // crashes. Preserving the data also round-trips it on save.
      if (!merged[workflow]) {
        (merged as unknown as Record<string, unknown>)[workflow] = {};
      }
      Object.assign(merged[workflow] as object, workflowOverrides);
    }
  }

  return merged;
}
